import { Injectable, Injector, computed, signal } from '@angular/core';

import * as _ from 'lodash';

import { Company, CompanySettings } from 'src/app/api/services/company';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { COMPANY_INTEGRATION_REGISTRY } from './integrations-company.registry';
import { CompanyService } from 'src/app/settings/services/company.service';
import { COMPANY_INTEGRATIONS } from '../models/integrations-company.catalog';
import {
  CompanyIntegration,
  CompanyIntegrationComponentService,
  ConnectedIntegrationRow,
} from '../models/integrations.types';

export type {
  CompanyIntegration,
  CompanyIntegrationField,
  ConnectedIntegrationRow,
} from '../models/integrations.types';
export { COMPANY_INTEGRATIONS } from '../models/integrations-company.catalog';

const DEFAULT_COMPANY_INTEGRATION_REQUIRE_ROLE = 'ua sa';

@Injectable({
  providedIn: 'root',
})
export class IntegrationsCompanyService {
  /** Catalog metadata merged once with feature registry wiring. */
  readonly integrations: CompanyIntegration[] = COMPANY_INTEGRATIONS.map(
    (entry) => ({
      ...entry,
      ...COMPANY_INTEGRATION_REGISTRY[entry.id],
    }),
  );

  private readonly _settings = signal<CompanySettings>({});
  readonly settings = this._settings.asReadonly();

  /** Connected flags for catalog entries that use `componentService`. */
  private readonly _componentEnabled = signal<Record<string, boolean>>({});

  readonly connected = computed(() => {
    const componentEnabled = this._componentEnabled();
    return this.integrations.filter((integration) => {
      if (!this.canAccessIntegration(integration)) {
        return false;
      }
      if (integration.configureMode === 'component') {
        return !!componentEnabled[integration.id];
      }
      return (
        !!integration.enabledKey &&
        this.isEnabled(this._settings()[integration.enabledKey])
      );
    });
  });

  /** Row view-model for the shared connected-row component. */
  readonly connectedRows = computed((): ConnectedIntegrationRow[] =>
    this.connected().map((integration) => ({
      id: integration.id,
      name: integration.name,
      logo: integration.logo,
    })),
  );

  readonly available = computed(() => {
    const connectedIds = new Set(this.connected().map((i) => i.id));
    return this.integrations.filter(
      (integration) =>
        this.canAccessIntegration(integration) &&
        !connectedIds.has(integration.id),
    );
  });

  private loadPromise: Promise<void> | null = null;

  constructor(
    private companyService: CompanyService,
    private companyStateService: CompanyStateService,
    private userStateService: UserStateService,
    private injector: Injector,
  ) {}

  async load(): Promise<void> {
    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = this.loadInternal().finally(() => {
      this.loadPromise = null;
    });

    return this.loadPromise;
  }

  /**
   * Disconnects an integration (no UI). Callers handle confirm + snackbars.
   */
  async disconnectIntegration(integration: CompanyIntegration): Promise<void> {
    if (integration.configureMode === 'component') {
      const componentService = this.getComponentService(integration);
      if (!componentService) {
        return;
      }
      await componentService.disconnect();
      this.refreshComponentConnected();
    } else if (integration.enabledKey) {
      await this.disableIntegration(integration.enabledKey);
    }
  }

  isEnabled(value: string | boolean | null | undefined): boolean {
    return value === true || value === 'true';
  }

  storedValues(integration: CompanyIntegration): Record<string, string> {
    const settings = this._settings();

    return (integration.fields ?? []).reduce<Record<string, string>>(
      (values, field) => {
        const stored = settings[field.key];
        values[field.key] = typeof stored === 'string' ? stored : '';
        return values;
      },
      {},
    );
  }

  exclusiveKeysToDisable(integration: CompanyIntegration): string[] {
    if (!integration.exclusiveWith) {
      return [];
    }

    const counterpart = this.connected().find(
      (i) => i.id === integration.exclusiveWith,
    );

    return counterpart?.enabledKey ? [counterpart.enabledKey] : [];
  }

  async saveIntegrationSettings(
    enabledKey: string,
    values: Record<string, string>,
    disableKeys: string[] = [],
  ): Promise<void> {
    const company = this.companyService.company;
    const oldCompany = _.cloneDeep(company);

    if (!company.settings) {
      company.settings = {};
    }

    Object.assign(company.settings, values);
    company.settings[enabledKey] = true;
    disableKeys.forEach((key) => {
      company.settings[key] = false;
    });

    await this.saveIntegrations(oldCompany);
  }

  /**
   * Disconnects an integration, keeping its credential values so a later
   * reconnect can pre-fill them. Providers whose credentials live in the OAuth
   * token provider lose theirs regardless: `save` deletes them server-side, and
   * their enabled state is derived from whether those credentials exist.
   */
  async disableIntegration(enabledKey: string): Promise<void> {
    const company = this.companyService.company;
    const oldCompany = _.cloneDeep(company);

    if (!company.settings) {
      company.settings = {};
    }

    company.settings[enabledKey] = false;

    await this.saveIntegrations(oldCompany);
  }

  private async loadInternal(): Promise<void> {
    const companyId = this.companyStateService.getSelectedCompanyId();
    if (!companyId) {
      this._settings.set({});
      this._componentEnabled.set({});
      return;
    }

    try {
      await this.companyService.getCompany(companyId);
      this.syncSettingsFromCompany();
    } catch (error) {
      console.error('Failed to load company integrations:', error);
    }
  }

  private syncSettingsFromCompany(): void {
    this._settings.set({ ...(this.companyService.company?.settings ?? {}) });
    this.syncComponentIntegrations(this.companyService.company);
  }

  getComponentService(
    integration: CompanyIntegration,
  ): CompanyIntegrationComponentService | null {
    if (!integration.componentService) {
      return null;
    }
    return this.injector.get(integration.componentService);
  }

  private syncComponentIntegrations(company: Company | null | undefined): void {
    for (const integration of this.integrations) {
      const componentService = this.getComponentService(integration);
      if (!componentService) {
        continue;
      }
      if (company && componentService.loadFromCompany) {
        componentService.loadFromCompany(company);
      } else {
        componentService.loadSettings();
      }
    }
    this.refreshComponentConnected();
  }

  private refreshComponentConnected(): void {
    const enabled: Record<string, boolean> = {};
    for (const integration of this.integrations) {
      const componentService = this.getComponentService(integration);
      if (componentService) {
        enabled[integration.id] = componentService.isEnabled();
      }
    }
    this._componentEnabled.set(enabled);
  }

  reloadComponentIntegration(integration: CompanyIntegration): void {
    const componentService = this.getComponentService(integration);
    if (!componentService) {
      return;
    }
    componentService.loadSettings();
    this.refreshComponentConnected();
  }

  findById(id: string): CompanyIntegration | undefined {
    return this.integrations.find((integration) => integration.id === id);
  }

  /**
   * Whether the current user may see/manage this catalog entry.
   * Same semantics as `*requireRole`.
   */
  canAccessIntegration(integration: CompanyIntegration): boolean {
    return this.userStateService.hasPermission(
      integration.requireRole ?? DEFAULT_COMPANY_INTEGRATION_REQUIRE_ROLE,
    );
  }

  /** Saves integration settings, rolling back in memory if the request fails. */
  private async saveIntegrations(oldCompany: any): Promise<void> {
    try {
      await this.companyService.save(null, oldCompany);
    } catch (error) {
      this.companyService.company.settings = oldCompany.settings;
      throw error;
    }

    this.companyService.clearCompanyCredentials();
    this.syncSettingsFromCompany();
  }
}
